import user from 'user';

import { TeacherApplicationContext } from '~shared/context/TeacherApplicationContext';
import { getActivity } from '~teacher/hook';
import { fetchAccount } from '~teacher/hook/account';
import {
  doImportBySoc,
  doJoinWorkspacePage,
  doQuizzesPage,
  renderPostPurchasePage,
} from '~teacher/pages';
import { queryClient } from '~teacher/queryClient';

import { VerificationStatus } from '../src/api/account/types';
import { dynamicImport } from '../src/util/dynamicImport';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const Backbone = require('backbone');
// eslint-disable-next-line @typescript-eslint/no-var-requires
const IntercomService = require('../intercom/IntercomService');

async function isLiveActivityRoute(activityInstanceId) {
  const activity = await getActivity(activityInstanceId);
  // check for data
  if (!activity.activity) return false;

  queryClient.setQueryData(['activity', activityInstanceId], () => activity);

  return activity.activity.endTime == null;
}

/**
 * Gets the user's email verification status
 * @returns {Promise<string>} - Returns the verification status
 */
async function getEmailVerificationStatus() {
  try {
    const account = await fetchAccount(queryClient);
    return account.verificationStatus;
  } catch (error) {
    console.error('Error fetching verification status:', error);
    return VerificationStatus.Unverified; // Don't block navigation on error
  }
}

const launchController = dynamicImport({
  moduleImport: () =>
    import(/* webpackChunkName: 'LaunchController' */ 'LaunchController'),
});

const roomsController = dynamicImport({
  moduleImport: () =>
    import(
      /* webpackChunkName: 'RoomsController' */
      'RoomsController'
    ),
});

const reportsController = dynamicImport({
  moduleImport: () =>
    import(
      /* webpackChunkName: 'ReportsController' */
      'ReportsController'
    ),
});

const spaceRaceController = dynamicImport({
  moduleImport: () =>
    import(
      /* webpackChunkName: 'SpaceRaceController' */
      'SpaceRaceController'
    ),
});

const editQuizController = dynamicImport({
  moduleImport: () =>
    import(
      /* webpackChunkName: 'EditQuizController' */
      'EditQuizController'
    ),
});

const profileController = dynamicImport({
  moduleImport: () =>
    import(/* webpackChunkName: 'ProfileController' */ 'ProfileController'),
});

const demographicsController = dynamicImport({
  moduleImport: () =>
    import(
      /* webpackChunkName: 'DemographicsController' */
      'DemographicsController'
    ),
});

const accountController = dynamicImport({
  moduleImport: () =>
    import(/* webpackChunkName: 'AccountController' */ 'AccountController'),
});

const activityResultController = dynamicImport({
  moduleImport: () =>
    import(
      /* 'ActivityResultController' */ '../src/teacher/controllers/ActivityResultController' // Should be '~teacher/controllers/ActivityResultController' but for some reason that alias wouldn't work -RP
    ),
});

const activity = dynamicImport({
  moduleImport: () => import(/* webpackChunkName: 'activity' */ 'activity'),
});

const workspaces = dynamicImport({
  moduleImport: () => import('../src/teacher/pages/WorkspacesPage'),
});

const workspaceQuizPage = dynamicImport({
  moduleImport: () => import('../src/teacher/pages/WorkspaceQuizPage'),
});

const discoverPage = dynamicImport({
  moduleImport: () => import('~teacher/pages/DiscoverPage'),
});

const pricingTablePage = dynamicImport({
  moduleImport: () => import('~teacher/pages/PricingTablePage'),
});

const importDoAccountPage = dynamicImport({
  moduleImport: () => import('~teacher/pages/AccountPage'),
});

const emailVerifyPage = dynamicImport({
  moduleImport: () => import('~teacher/pages/VerifyEmailPage'),
});

const confirmEmailPage = dynamicImport({
  moduleImport: () => import('~teacher/pages/ConfirmEmailPage'),
});

let TeacherRouter = Backbone.Router.extend({
  routes: {
    '': 'teacher',
    'teacher-login/:authToken': 'mcSsoLogin',
    launch: 'launch',
    'quizzes(/*quizFolderPath)': 'quizzes',
    'workspaces/:workspaceId/quizzes/:workspaceQuizId': 'workspaceQuiz',
    'workspaces/:workspaceId(/:pageId)(/:itemId)': 'workspaces',
    'import-quiz/:socNumber': 'importQuiz',
    'import-quiz/:socNumber/quizshop': 'importQuizFromShop',
    'join-workspace/:workspaceCode': 'joinWorkspace',
    'invite/(:inviteCode)': 'invite',
    'edit-quiz/:socNumber': 'editQuiz',
    'edit-quiz-v2/:socNumber': 'editQuiz',
    'space-race': 'spaceRace',
    'profile/info': 'profileInfo',
    'profile/demo': 'profileDemo',
    'profile/billing': 'profileBilling',
    'profile/purchase': 'profilePurchase',
    'profile/apply': 'profileApplyLicense',
    'post-purchase/:teamId': 'postPurchase',
    discover: 'discover',
    pricing: 'pricing',

    // These are legacy routes... we need to keep them here for spacerace and for old bookmarks.
    'live-results': 'liveResultsTable',
    'live-results/table': 'liveResultsTable',
    'live-results/question/:number': 'liveResultsQuestion',
    'final-results/:activityId/:quizId/table': 'finalResultsTable',
    'final-results/:activityId/:quizId/question/:number':
      'finalResultsQuestion',
    'results/live': 'liveActivityResults',
    'results(/:activityId)': 'activityResult',
    'results/:activityId/questions/:questionPosition': 'activityResultQuestion',

    reports: 'reports',
    rooms: 'rooms',

    'email-verify': 'emailVerify',
    'confirm-email': 'confirmEmail',

    '*bad': 'teacher', // This handles all invalid routes.
  },

  /**
   * List of routes that should be accessible even when email verification is required
   */
  publicRoutes: ['confirm-email', 'email-verify'],

  initialize: function () {
    this.lastRoute = null;
    this.listenTo(this, 'route', this.onRouteChange);
  },

  /**
   * When the route changes, remember the last route.
   * Note: This executes after the new route's callback method has been executed. However, if the callback
   *       loads a controller on demand, this will be executed before the asynchronous load finishes.
   * @param callbackName The name of the route callback function
   * @param callbackArgs The arguments, if any, to the route callback function
   */
  onRouteChange: function (callbackName, callbackArgs) {
    if (router.lastRoute !== Backbone.history.fragment) {
      router.lastRoute = Backbone.history.fragment;
    }
  },

  /**
   * Returns the current url, from the context of the router.
   */
  current: function () {
    return Backbone.history.fragment;
  },

  /**
   * This is a property that allows child components to perform actions, before the route handling is executed.
   * If you return True from this method navigation is blocked.
   */
  onBeforeExecute: null,

  execute: function (callback, args, name) {
    const currentRoute = Backbone.history.fragment;
    const isPublicRoute = this.publicRoutes.some((route) =>
      currentRoute.startsWith(route)
    );

    if (!isPublicRoute) {
      getEmailVerificationStatus().then((verificationStatus) => {
        if (verificationStatus === VerificationStatus.Unverified) {
          this.navigate('email-verify', { trigger: true, replace: true });
          return;
        } else if (verificationStatus === VerificationStatus.SoftVerify) {
          this.navigate('confirm-email', { trigger: true, replace: true });
          return;
        }

        this.processRoute(callback, args, name);
      });
      return;
    }

    return this.processRoute(callback, args, name);
  },

  processRoute: function (callback, args, name) {
    if (
      router.onBeforeExecute &&
      router.onBeforeExecute(callback, args, name)
    ) {
      return false;
    }

    if (callback) callback.apply(this, args);
  },

  /**
   * Return true if the app is routing into the profile from outside the profile, false otherwise.
   * @returns {boolean}
   */
  isEnteringProfile: function () {
    return router.lastRoute === null || !/profile/.test(router.lastRoute);
  },

  /* ----------------------------- */
  /*    Route Handler Methods    */
  /* ----------------------------- */

  teacher: function () {
    router.routeToLaunchAndReplace();
  },

  mcSsoLogin: function () {
    // MC SSO login is handled in the teacher.js entry point.
    if (user.getSharedSocNumber()) {
      router.routeToImportQuiz(user.getSharedSocNumber());
    } else {
      router.routeToLaunchAndReplace();
    }
  },

  launch: function () {
    launchController().then((launchController) => {
      IntercomService.update();
      launchController.doLaunch(router);
    });
  },

  workspaces: function (workspaceId, pageId, itemId) {
    IntercomService.update();
    workspaces().then((doWorkspaces) =>
      doWorkspaces(workspaceId, router, pageId, itemId)
    );
  },

  quizzes: function (quizFolderPath) {
    IntercomService.update();
    doQuizzesPage({
      router: router,
      quizFolderPath: quizFolderPath,
    });
  },

  importQuiz: function (sharedSocNumber, params) {
    doImportBySoc({
      socNumber: sharedSocNumber,
      isFromQuizShop: false,
      router: router,
    });
  },

  importQuizFromShop: function (sharedSocNumber, params) {
    doImportBySoc({
      socNumber: sharedSocNumber,
      isFromQuizShop: true,
      router: router,
    });
  },

  joinWorkspace: function (workspaceCode) {
    doJoinWorkspacePage({
      router: router,
      workspaceCode: workspaceCode,
    });
  },

  editQuiz: function (socNumber, params) {
    editQuizController().then((editQuizController) => {
      editQuizController.doEditQuiz(socNumber, router);
    });
  },

  spaceRace: function () {
    spaceRaceController().then((spaceRaceController) => {
      spaceRaceController.doSpaceRace(router);
    });
  },

  reports: function () {
    reportsController().then((reportsController) => {
      IntercomService.update();
      reportsController.doReports(router);
    });
  },

  rooms: function () {
    roomsController().then((roomsController) => {
      IntercomService.update();
      roomsController.doRooms(router);
    });
  },

  profileInfo: function () {
    profileController().then((profileController) => {
      profileController.doProfile(router);
    });
  },

  profileDemo: function () {
    demographicsController().then((demographicsController) => {
      demographicsController.doDemo(router);
    });
  },

  profileBilling: function () {
    accountController().then((accountController) => {
      accountController.doAccount(router);
    });
  },

  profilePurchase: function () {
    accountController().then((accountController) => {
      accountController.doAccount(router, true);
    });
  },

  profileApplyLicense: function () {
    accountController().then((accountController) => {
      accountController.doAccount(router, false, true);
    });
  },

  postPurchase: function (teamId) {
    renderPostPurchasePage({ teamId: teamId });
  },

  emailVerify: function () {
    emailVerifyPage().then((doEmailVerifyPage) => {
      doEmailVerifyPage();
    });
  },

  confirmEmail: function () {
    confirmEmailPage().then((doConfirmEmailPage) => {
      doConfirmEmailPage();
    });
  },

  // To be deleted post Results Grid full release
  liveResultsTable: function () {
    router.liveActivityResults();
  },

  liveResultsQuestion: function (number) {
    router.liveActivityResults();
  },

  finalResultsTable: function (activityId, quizId) {
    // Ensure inputs are numbers
    let activityInstanceId = parseInt(activityId);
    router.activityResult(activityInstanceId);
  },

  finalResultsQuestion: function (activityId, quizId, number) {
    // Ensure inputs are numbers
    let activityInstanceId = parseInt(activityId);
    let questionPosition = parseInt(number);
    router.activityResultQuestion(activityInstanceId, questionPosition);
  },

  activityResult: function (activityInstanceId) {
    if (!activityInstanceId) {
      this.liveActivityResults();
      return;
    }
    IntercomService.update();
    // Ensure inputs are numbers
    activityInstanceId = parseInt(activityInstanceId);

    activityResultController().then((controller) => {
      controller.doActivityResult({
        activityInstanceId,
        socrativeRouter: router,
        teacherApplicationContext: TeacherApplicationContext.Socrative,
      });
    });
  },

  activityResultQuestion: function (activityInstanceId, questionPosition) {
    // ensure inputs are numbers
    activityInstanceId = parseInt(activityInstanceId, 10);
    questionPosition = parseInt(questionPosition, 10);

    if (isNaN(activityInstanceId) || isNaN(questionPosition)) {
      console.error('could parse activity id or question position');
      router.routeToReports();
    }

    activityResultController().then((activityResultController) => {
      activityResultController.doActivityResultQuestion({
        activityInstanceId,
        questionPosition,
        socrativeRouter: router,
        teacherApplicationContext: TeacherApplicationContext.Socrative,
      });
    });
  },

  liveActivityResults: function () {
    IntercomService.update();
    activityResultController().then((activityResultController) =>
      activityResultController.doLiveActivityResult(router)
    );
  },

  workspaceQuiz: function (workspaceId, workspaceQuizId) {
    IntercomService.update();
    workspaceQuizPage().then((workspaceQuizPage) =>
      workspaceQuizPage({
        workspaceId: workspaceId,
        workspaceQuizId: workspaceQuizId,
        router: router,
      })
    );
  },

  discover: function () {
    IntercomService.update();
    discoverPage().then((discoverPage) => discoverPage());
  },

  pricing: function () {
    pricingTablePage().then((pricingTablePage) => pricingTablePage());
  },

  invite: function (inviteCode) {
    const invite = inviteCode;
    try {
      const data = window.atob(invite);
      // Decode the invite code
      const { invite_code: inviteCode } = JSON.parse(data);
      launchController().then((launchController) => {
        launchController.doLaunch(router, inviteCode);

        router.navigate('launch', { trigger: false });
        IntercomService.update();
      });
    } catch (e) {
      if (__DEV__) {
        console.error('Error decoding invite code', e);
      }
      launchController().then((launchController) => {
        launchController.doLaunch(router, 'error');
        router.navigate('launch', { trigger: false });
        IntercomService.update();
      });
      return;
    }
  },

  /* -------------------------- */
  /*    Navigation Methods    */
  /* -------------------------- */

  routeToTeacherLogin: function () {
    window.location.href = '/login/teacher/';
  },

  routeToProfileInfo: function () {
    router.navigate('profile/info', {
      trigger: true,
    });
  },

  routeToProfileDemo: function () {
    router.navigate('profile/demo', {
      trigger: true,
    });
  },

  routeToProfileBilling: function () {
    router.navigate('profile/billing', {
      trigger: true,
    });
  },

  routeToProfilePurchase: function () {
    router.navigate('profile/purchase', {
      trigger: true,
    });
  },

  routeToProfileApplyLicense: function () {
    router.navigate('profile/apply', {
      trigger: true,
    });
  },

  routeToPricing: function () {
    router.navigate('pricing', {
      trigger: true,
    });
  },

  routeToLaunch: function () {
    router.navigate('launch', {
      trigger: true,
    });
  },

  routeToLaunchAndReplace: function () {
    router.navigate('launch', {
      trigger: true,
      replace: true,
    });
  },

  routeToLiveResults: function (data) {
    // TODO: Consider refactoring this logic??? -RLP
    if (data && data.number) {
      router.navigate('live-results/question/' + data.number, {
        trigger: true,
      });
    } else {
      // Ideally the router would not be aware of activity logic, but this code provides a central
      // location for determining where to route when any activity is in progress (see SOC-566).
      activity().then((activity) => {
        if (activity.isNonSpaceRace()) {
          let route = 'live-results/table';
          if (activity.isTeacherPaced()) {
            route =
              'live-results/question/' + activity.getCurrentQuestionNumber();
          }
          router.navigate(route, {
            trigger: true,
          });
        } else if (activity.isSpaceRace()) {
          router.routeToSpaceRace();
        }
      });
    }
  },

  routeToResults: function (activityInstanceId) {
    router.navigate(`results/${activityInstanceId}`, { trigger: true });
  },

  routeToResultsQuestion: function (
    activityInstanceId,
    questionPosition,
    shouldReplace = false
  ) {
    router.navigate(
      `results/${activityInstanceId}/questions/${questionPosition}`,
      {
        trigger: true,
        replace: shouldReplace,
      }
    );
  },

  routeToFinalResultsTable: function (data) {
    router.navigate(
      'final-results/' + data.activityId + '/' + data.quizId + '/table',
      {
        trigger: true,
      }
    );
  },

  routeToFinalResultsQuestion: function (data) {
    router.navigate(
      'final-results/' +
        data.activityId +
        '/' +
        data.quizId +
        '/question/' +
        data.number,
      {
        trigger: true,
      }
    );
  },

  routeToEditQuiz: function (socNumber) {
    router.navigate(`edit-quiz/${socNumber}`, {
      trigger: true,
    });
  },

  /**
   * Route to the quiz list view.
   */
  routeToQuizzes: function () {
    router.navigate('quizzes?v2', {
      trigger: true,
    });
  },

  /**
   * Route to the quiz list view.
   */
  routeToQuizzesAndReplace: function () {
    router.navigate('quizzes', {
      trigger: true,
      replace: true,
    });
  },

  routeToWorkspace: function (workspaceId) {
    router.navigate(`workspaces/${workspaceId}`, {
      trigger: true,
    });
  },

  routeToRooms: function () {
    router.navigate('rooms', {
      trigger: true,
    });
  },

  routeToReports: function () {
    router.navigate('reports', {
      trigger: true,
    });
  },

  routeToDiscovery: function () {
    router.navigate('discover', { trigger: true });
  },

  routeToSpaceRace: function (shouldReplace = false) {
    router.navigate('space-race', {
      trigger: true,
      replace: shouldReplace,
    });
  },

  routeToDynamic: function (data) {
    router.navigate(data.route, {
      trigger: true,
    });
  },
});

let router = new TeacherRouter();

if (window.top === window.self) {
  window.SOCRATIVE = window.SOCRATIVE || {};
  window.SOCRATIVE.teacherRouter ??= router;
}

export default router;
